import { useEffect, useMemo, useState } from "react"

import { ApiError, apiClient, type IncidentEscalationPacketPublicPayload } from "@/lib/api/client"

type PacketViewState = "expired" | "loading" | "ready" | "unavailable"

interface PublicIncidentEscalationPacketPageProps {
  initialToken: string
}

function installNoIndexMetaTag() {
  if (typeof document === "undefined") {
    return () => {}
  }

  const existing = document.querySelector('meta[name="robots"]')
  const tag = existing instanceof HTMLMetaElement ? existing : document.createElement("meta")

  tag.name = "robots"
  tag.content = "noindex, nofollow"

  if (!existing) {
    document.head.appendChild(tag)
  }

  return () => {
    if (!existing && tag.parentElement) {
      tag.parentElement.removeChild(tag)
    }
  }
}

function renderDate(value?: string | null) {
  if (!value) {
    return "Not available"
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return "Not available"
  }

  return parsed.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  })
}

function renderNumber(value?: number | null) {
  if (value === undefined || value === null) {
    return "Not available"
  }

  return String(value)
}

function toTitle(value: string | null | undefined) {
  if (!value) {
    return "Not available"
  }

  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

export function PublicIncidentEscalationPacketPage({
  initialToken,
}: PublicIncidentEscalationPacketPageProps) {
  const [state, setState] = useState<PacketViewState>("loading")
  const [payload, setPayload] = useState<IncidentEscalationPacketPublicPayload | null>(null)

  useEffect(() => {
    const removeMetaTag = installNoIndexMetaTag()

    return () => {
      removeMetaTag()
    }
  }, [])

  useEffect(() => {
    let active = true

    async function loadPacket() {
      setState("loading")
      setPayload(null)

      try {
        const response = await apiClient.getIncidentEscalationPacket(initialToken)

        if (!active) {
          return
        }

        setPayload(response.data)
        setState("ready")
      } catch (error) {
        if (!active) {
          return
        }

        if (error instanceof ApiError && error.status === 410) {
          setState("expired")
          return
        }

        setState("unavailable")
      }
    }

    void loadPacket()

    return () => {
      active = false
    }
  }, [initialToken])

  const incidentReference = payload?.header?.incidentReference || payload?.packet?.tokenPreview || "Incident"
  const pageTitle = useMemo(() => {
    if (state === "ready") {
      return `${incidentReference} • Incident Escalation Packet`
    }

    if (state === "expired") {
      return "Incident Escalation Packet Expired"
    }

    return "Incident Escalation Packet"
  }, [incidentReference, state])

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = pageTitle
    }
  }, [pageTitle])

  if (state === "loading") {
    return (
      <main className="mx-auto min-h-screen w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-1/2 rounded bg-slate-200" />
          <div className="h-24 w-full rounded bg-slate-200" />
          <div className="h-56 w-full rounded bg-slate-200" />
        </div>
      </main>
    )
  }

  if (state === "expired") {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-3xl items-center justify-center px-4 py-10 sm:px-6">
        <section className="w-full rounded-2xl border border-amber-300 bg-amber-50 p-8 text-center shadow-sm">
          <h1 className="text-2xl font-semibold text-amber-900">Packet access has expired</h1>
          <p className="mt-3 text-sm text-amber-800">
            This Incident Escalation Packet link is no longer active. Request a fresh packet link from the SafetyQR operator.
          </p>
        </section>
      </main>
    )
  }

  if (state === "unavailable" || !payload) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-3xl items-center justify-center px-4 py-10 sm:px-6">
        <section className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-8 text-center shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">Packet unavailable</h1>
          <p className="mt-3 text-sm text-slate-700">
            This Incident Escalation Packet link is not available. Verify the link and request a new share if needed.
          </p>
        </section>
      </main>
    )
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">SafetyQR Authority Packet</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">{payload.header?.incidentReference}</h1>
        <div className="mt-4 grid gap-3 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-4">
          <p><span className="font-medium text-slate-900">Severity:</span> {toTitle(payload.header?.severity)}</p>
          <p><span className="font-medium text-slate-900">Status:</span> {toTitle(payload.header?.incidentStatus)}</p>
          <p><span className="font-medium text-slate-900">Generated:</span> {renderDate(payload.header?.generatedAt)}</p>
          <p><span className="font-medium text-slate-900">Expires:</span> {renderDate(payload.header?.expiresAt)}</p>
        </div>
      </header>

      <section className="mt-6 grid gap-6 lg:grid-cols-2">
        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Location</h2>
          <dl className="mt-3 space-y-2 text-sm text-slate-700">
            <div><dt className="font-medium text-slate-900">Label</dt><dd>{payload.location?.label || "Not available"}</dd></div>
            <div><dt className="font-medium text-slate-900">Jurisdiction / Organization</dt><dd>{payload.location?.jurisdictionOrOrganizationName || "Not available"}</dd></div>
            <div><dt className="font-medium text-slate-900">State</dt><dd>{payload.location?.state || "Not available"}</dd></div>
            <div>
              <dt className="font-medium text-slate-900">Coordinates</dt>
              <dd>
                {payload.location?.coordinates
                  ? `${payload.location.coordinates.latitude}, ${payload.location.coordinates.longitude}`
                  : "Not available"}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-900">Map</dt>
              <dd>
                {payload.location?.mapUrl ? (
                  <a className="text-blue-700 underline" href={payload.location.mapUrl} rel="noreferrer" target="_blank">
                    Open map link
                  </a>
                ) : "Not available"}
              </dd>
            </div>
          </dl>
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Incident Summary</h2>
          <dl className="mt-3 space-y-2 text-sm text-slate-700">
            <div><dt className="font-medium text-slate-900">Type</dt><dd>{toTitle(payload.incidentSummary?.type)}</dd></div>
            <div><dt className="font-medium text-slate-900">Report Kind</dt><dd>{toTitle(payload.incidentSummary?.reportKind)}</dd></div>
            <div><dt className="font-medium text-slate-900">Injury Status</dt><dd>{toTitle(payload.incidentSummary?.injuryStatus)}</dd></div>
            <div><dt className="font-medium text-slate-900">People Involved</dt><dd>{renderNumber(payload.incidentSummary?.peopleInvolved)}</dd></div>
            <div><dt className="font-medium text-slate-900">Occurred At</dt><dd>{renderDate(payload.incidentSummary?.occurredAt)}</dd></div>
            <div><dt className="font-medium text-slate-900">Reported At</dt><dd>{renderDate(payload.incidentSummary?.reportedAt)}</dd></div>
            <div><dt className="font-medium text-slate-900">Description</dt><dd>{payload.incidentSummary?.description || "Not available"}</dd></div>
          </dl>
        </article>
      </section>

      <section className="mt-6 grid gap-6 lg:grid-cols-2">
        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Subject Context</h2>
          <dl className="mt-3 space-y-2 text-sm text-slate-700">
            <div><dt className="font-medium text-slate-900">Organization</dt><dd>{payload.subjectContext?.organizationName || "Not available"}</dd></div>
            <div><dt className="font-medium text-slate-900">Subject Label</dt><dd>{toTitle(payload.subjectContext?.subjectLabel)}</dd></div>
            <div><dt className="font-medium text-slate-900">Asset Reference</dt><dd>{payload.subjectContext?.assetReference || "Not available"}</dd></div>
            <div><dt className="font-medium text-slate-900">Vehicle Plate</dt><dd>{payload.subjectContext?.vehiclePlate || "Not available"}</dd></div>
            <div><dt className="font-medium text-slate-900">Preferred Language</dt><dd>{toTitle(payload.subjectContext?.preferredLanguage)}</dd></div>
          </dl>
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Dispatch</h2>
          <p className="mt-3 text-sm text-slate-700">
            <span className="font-medium text-slate-900">Total Assignments:</span> {renderNumber(payload.dispatch?.dispatchSummary?.total)}
          </p>
          <p className="mt-1 text-sm text-slate-700">
            <span className="font-medium text-slate-900">Match Strategy:</span> {toTitle(payload.dispatch?.jurisdictionMatchStrategy)}
          </p>
          <p className="mt-1 text-sm text-slate-700">
            <span className="font-medium text-slate-900">Last Operator Note:</span> {payload.dispatch?.lastOperatorNote?.updateText || "Not available"}
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            {(payload.dispatch?.assignments || []).map((assignment) => (
              <li className="rounded border border-slate-200 p-2" key={assignment.id || `${assignment.agencyKind}-${assignment.notifiedAt}`}>
                <p><span className="font-medium text-slate-900">Agency:</span> {toTitle(assignment.agencyKind)}</p>
                <p><span className="font-medium text-slate-900">Status:</span> {toTitle(assignment.status)}</p>
                <p><span className="font-medium text-slate-900">Rule:</span> {toTitle(assignment.dispatchRule)}</p>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Evidence</h2>
        <p className="mt-2 text-sm text-slate-700">
          <span className="font-medium text-slate-900">Photos:</span> {renderNumber(payload.evidence?.photoCount)}
          {" • "}
          <span className="font-medium text-slate-900">Voice Notes:</span> {renderNumber(payload.evidence?.voiceNoteCount)}
          {" • "}
          <span className="font-medium text-slate-900">Chain of Custody Entries:</span> {renderNumber(payload.evidence?.chainOfCustodyCount)}
        </p>
        {(payload.evidence?.photos || []).length > 0 ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(payload.evidence?.photos || []).map((photo) => (
              <figure className="overflow-hidden rounded border border-slate-200" key={photo.id || photo.url}>
                {photo.url ? (
                  <img alt={photo.fileName || "Incident evidence photo"} className="h-40 w-full object-cover" src={photo.url} />
                ) : (
                  <div className="flex h-40 items-center justify-center bg-slate-100 text-xs text-slate-500">Image unavailable</div>
                )}
                <figcaption className="p-2 text-xs text-slate-600">{photo.fileName || "Unnamed photo"}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-600">No photo evidence attached.</p>
        )}
      </section>

      <footer className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 shadow-sm">
        <p><span className="font-medium text-slate-900">Packet ID:</span> {payload.footer?.packetId || "Not available"}</p>
        <p><span className="font-medium text-slate-900">Packet Version:</span> {renderNumber(payload.footer?.packetVersion)}</p>
        <p><span className="font-medium text-slate-900">Audit Timestamp:</span> {renderDate(payload.footer?.auditTimestamp)}</p>
        <p className="mt-2 text-xs text-slate-600">{payload.footer?.disclaimer || "SafetyQR Incident Escalation Packet is read-only."}</p>
      </footer>
    </main>
  )
}
